import { Button, FormGroup, InputGroup } from "@blueprintjs/core"
import CustomDialog from "components/Atoms/CustomDialog"
import StaticSelector from "components/Atoms/StaticSelector"
import React, { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { ADD_PACKING_ALLOTMENT } from "graphql/production/allocation.mutation.gql"
import AppToaster from "utils/AppToaster"

export const ReverseModal = ({
  refetch,
  inwardAllocationId,
  line,
  skuCapacity,
}: {
  refetch?: () => void
  inwardAllocationId: string
  line: any
  skuCapacity: number
}) => {
  const [state, setState] = useState<any>({})
  const [errors, setErrors] = useState<any>({})
  const [params, setParams] = useSearchParams()
  const lineId = params.get("lineId")

  function updateState(path: string, value: any) {
    setState(val => ({ ...val, [path]: value }))
  }

  const onClose = () => {
    params.delete("lineId")
    setParams(params)
    setState({})
    setErrors({})
  }

  const reverseWeight = Number(state?.quantity || 0) * (skuCapacity ?? 0)

  // 👇 run live validation on changes
useEffect(() => {
  const newErrors: any = {}

  const maxCrates = Number(line?.allotedQuantity) || 0
  const maxWeight = Number(line?.allotedWeight) || 0

  if (state?.quantity && state?.quantity <= 0) {
    newErrors.quantity = "Please enter valid reverse quantity"
  }
  if (state?.quantity > maxCrates) {
    const excess = state?.quantity - maxCrates
    newErrors.quantity = `Reverse quantity cannot exceed allotted (${maxCrates}). Reduce by ${excess} crates`
  }
  if (reverseWeight > maxWeight) {
    const excess = (reverseWeight - maxWeight).toFixed(2)
    newErrors.weight = `Reverse weight cannot exceed allotted (${maxWeight} kg). Reduce by ${excess} kg`
  }
  if (state?.reason === undefined || !state?.reason?.id) {
    newErrors.reason = "Please select a reason"
  }

  setErrors(newErrors)
}, [state, reverseWeight, line])

  return (
    <CustomDialog
      isOpen={Boolean(lineId)}
      onClose={onClose}
      icon={"label"}
      title="Reverse to Holding"
      footerRightButton={
        <ButtonMutation
          mutation={ADD_PACKING_ALLOTMENT}
          variables={{
            id: lineId,
            allotment: {
              inwardAllocationId,
              productionLineId: line?.productionLine?.id,
              supervisorId: line?.supervisor?.id,
              allotedQuantity:
                Number(line?.allotedQuantity) - Number(state?.quantity),
              allotedWeight: Number(line?.allotedWeight) - reverseWeight,
              reverseQuantity: Number(state?.quantity),
              reverseWeight,
              reversalReason: state?.reason?.id,
            },
          }}
          validate={() => Object.keys(errors).length === 0}
          onCompleted={() => {
            AppToaster.show({
              message: "Reversed successfully",
              icon: "tick",
              intent: "success",
            })
            refetch?.()
            onClose()
          }}
          onError={() => {
            AppToaster.show({
              message: "Unable to reverse",
              icon: "cross",
              intent: "danger",
            })
          }}
          text="Confirm"
          intent="danger"
        />
      }
    >
      {/* Crate input */}
      <FormGroup
        className="mb-5"
        label={
          <>
            Reverse no. of Crate <span className="text-red">*</span>
          </>
        }
        helperText={
          errors.quantity ? (
            <span className="text-red-600">{errors.quantity}</span>
          ) : (
            <span className="text-[#1F6D99] font-400">
              {`Max: ${line?.allotedQuantity ?? 0} Crates`}
            </span>
          )
        }
      >
        <InputGroup
          type="number"
          required
          value={state?.quantity ?? ""}
          onChange={e => updateState("quantity", Number(e.target.value))}
        />
      </FormGroup>

      {/* Weight input */}
      <FormGroup
        className="mb-5"
        label={
          <>
            Reverse Weight (kg) <span className="text-red">*</span>
          </>
        }
        helperText={
          errors.weight ? (
            <span className="text-red-600">{errors.weight}</span>
          ) : (
            <span className="text-[#1F6D99]">{`Max: ${
              line?.allotedWeight ?? 0
            } kg`}</span>
          )
        }
      >
        <InputGroup
          type="number"
          required
          disabled
          value={reverseWeight?.toFixed(2)}
        />
      </FormGroup>

      {/* Reason selector */}
      <StaticSelector
        label="Reason"
        required
        items={[
          { id: "Damaged Crates", name: "Damaged Crates" },
          { id: "Line Error", name: "Line Error" },
          { id: "Other", name: "Other" },
        ]}
        value={state?.reason}
        onChange={(_, e) => updateState("reason", e)}
        helperText={
          errors.reason && <span className="text-red-600">{errors.reason}</span>
        }
      />
    </CustomDialog>
  )
}
